import apiService from "./apiService";
import axiosInstance from "../Axiosinstance";
import { ENDPOINTS } from "../Endpoints";
import { uploadFile, uploadFiles, UPLOAD_PURPOSES } from "../../utils/directUpload";

const isFileLike = (value) =>
  (typeof File !== "undefined" && value instanceof File) ||
  (typeof Blob !== "undefined" && value instanceof Blob);

/**
 * Swap a File in the payload for the publicId of a browser-side upload.
 * Callers keep passing Files; only the wire format changed.
 */
const withUploadedProfilePicture = async (payload = {}, options = {}) => {
  const { profilePicture, ...rest } = payload;
  if (!isFileLike(profilePicture)) return rest;

  const { publicId } = await uploadFile(
    profilePicture,
    UPLOAD_PURPOSES.PROFILE_PICTURE,
    options
  );
  return { ...rest, publicId };
};

/** Same idea for the vehicle's image and document collections. */
const withUploadedVehicleMedia = async (payload = {}, options = {}) => {
  const { vehicleImages, vehicleDocuments, ...rest } = payload;
  const body = { ...rest };

  const images = Array.from(vehicleImages || []).filter(isFileLike);
  if (images.length) {
    const uploaded = await uploadFiles(images, UPLOAD_PURPOSES.VEHICLE_IMAGE, options);
    body.vehicleImagePublicIds = uploaded.map((item) => item.publicId);
  }

  const documents = Array.from(vehicleDocuments || []).filter(isFileLike);
  if (documents.length) {
    const uploaded = await uploadFiles(
      documents,
      UPLOAD_PURPOSES.VEHICLE_DOCUMENT,
      options
    );
    body.vehicleDocumentPublicIds = uploaded.map((item) => item.publicId);
  }

  return body;
};

const unwrapProfile = (res) =>
  res?.data?.data?.profile ||
  res?.data?.data ||
  res?.data?.profile ||
  res?.profile ||
  res?.data ||
  res ||
  {};

class ProvidersService {
  /* ================= PROFILE ================= */

  async getProfile(options = {}) {
    const res = await apiService.get(
      ENDPOINTS.PROVIDERS.PROFILE,
      { ...(options.params || {}), _t: Date.now() },
    );

    return unwrapProfile(res);
  }

  async upsertProfile(payload = {}) {
    const body = await withUploadedProfilePicture(payload);
    const res = await axiosInstance.post(ENDPOINTS.PROVIDERS.PROFILE, body);
    return res.data;
  }

  async updateProfile(payload = {}) {
    const body = await withUploadedProfilePicture(payload);
    const res = await axiosInstance.put(ENDPOINTS.PROVIDERS.PROFILE, body);
    return res.data;
  }

  /* =============== DOCUMENTS (LICENSE) =============== */

  // Files are uploaded to Cloudinary from the browser (see utils/directUpload),
  // so these endpoints receive only the resulting publicId, which the API
  // verifies against Cloudinary before storing it.
  async uploadLicense(license, options = {}) {
    const { publicId } = await uploadFile(
      license,
      UPLOAD_PURPOSES.PROVIDER_LICENSE,
      options
    );
    const res = await axiosInstance.post(ENDPOINTS.PROVIDERS.LICENSE, { publicId });
    return res.data;
  }

  async updateLicense(license, options = {}) {
    const { publicId } = await uploadFile(
      license,
      UPLOAD_PURPOSES.PROVIDER_LICENSE,
      options
    );
    const res = await axiosInstance.put(ENDPOINTS.PROVIDERS.LICENSE, { publicId });
    return res.data;
  }

  // ✅ READ FROM PROFILE
  async getDocuments() {
    const profile = await this.getProfile();
    if (!profile) return {};
    return profile.documents || profile;
  }

  /* ================= VEHICLE ================= */

  async submitVehicle(payload = {}, options = {}) {
    const body = await withUploadedVehicleMedia(payload, options);
    const res = await axiosInstance.post(ENDPOINTS.PROVIDERS.VEHICLE, body);
    return res.data;
  }

  async updateVehicle(payload = {}, options = {}) {
    const body = await withUploadedVehicleMedia(payload, options);
    const res = await axiosInstance.put(ENDPOINTS.PROVIDERS.VEHICLE, body);
    return res.data;
  }

  // ✅ READ FROM PROFILE
  async getVehicle() {
    const profile = await this.getProfile();
    return (
      profile.vehicle ||
      profile.vehicleInformation ||
      profile.vehicle_info ||
      {}
    );
  }

  /* ================= PAYMENT ================= */

  async submitPayment(payload = {}) {
    const res = await axiosInstance.post(
      ENDPOINTS.PROVIDERS.PAYMENT,
      payload
    );
    return res.data;
  }

  async updatePayment(payload = {}) {
    const res = await axiosInstance.put(
      ENDPOINTS.PROVIDERS.PAYMENT,
      payload
    );
    return res.data;
  }

  // ✅ READ FROM PROFILE
  async getPayment() {
    const profile = await this.getProfile();
    return (
      profile.payment ||
      profile.pricingAndAccount ||
      profile.bankAccount ||
      {}
    );
  }

  /* ================= EXPERTISE (ONLY ONE WITH GET) ================= */

  async getExpertise() {
    const res = await axiosInstance.get(ENDPOINTS.PROVIDERS.EXPERTISE);
    return res.data;
  }

  async submitExpertise(payload = {}) {
    const res = await axiosInstance.post(
      ENDPOINTS.PROVIDERS.EXPERTISE,
      payload
    );
    return res.data;
  }

  async updateExpertise(payload = {}) {
    const res = await axiosInstance.put(
      ENDPOINTS.PROVIDERS.EXPERTISE,
      payload
    );
    return res.data;
  }

  /* ================= BANKS ================= */

  async getBanks() {
    const res = await axiosInstance.get(ENDPOINTS.PROVIDERS.BANKS);
    return res.data;
  }

  async resolveBankAccount(params = {}) {
    const res = await axiosInstance.get(
      ENDPOINTS.PROVIDERS.BANK_RESOLVE,
      { params }
    );
    return res.data;
  }

  /* ================= ID DOCUMENT UPLOAD ================= */

  async uploadIdDocument(idDocumentFile, options = {}) {
    const { publicId } = await uploadFile(
      idDocumentFile,
      UPLOAD_PURPOSES.PROVIDER_ID_DOCUMENT,
      options
    );
    const res = await axiosInstance.post(ENDPOINTS.PROVIDERS.ID_DOCUMENT, { publicId });
    return res.data;
  }

  /* ================= PROFILE SUBMIT REVIEW ================= */

  async submitProfileReview() {
    const res = await axiosInstance.post(ENDPOINTS.PROVIDERS.SUBMIT_REVIEW);
    return res.data;
  }

  /* ================= AVAILABILITY ================= */

  async getAvailability() {
    const res = await axiosInstance.get(ENDPOINTS.PROVIDERS.AVAILABILITY);
    return res.data;
  }

  async updateAvailability(isOnline) {
    const res = await axiosInstance.patch(
      ENDPOINTS.PROVIDERS.AVAILABILITY,
      { isOnline }
    );
    return res.data;
  }

  /* ================= BANK ACCOUNTS CRUD ================= */

  async getBankAccounts() {
    const res = await axiosInstance.get(ENDPOINTS.PROVIDERS.BANK_ACCOUNTS);
    return res.data;
  }

  async createBankAccount(payload = {}) {
    const res = await axiosInstance.post(
      ENDPOINTS.PROVIDERS.BANK_ACCOUNTS,
      payload
    );
    return res.data;
  }

  async deleteBankAccount(accountId) {
    const res = await axiosInstance.delete(
      `${ENDPOINTS.PROVIDERS.BANK_ACCOUNTS}/${accountId}`
    );
    return res.data;
  }

  async setDefaultBankAccount(accountId) {
    const res = await axiosInstance.patch(
      `${ENDPOINTS.PROVIDERS.BANK_ACCOUNTS}/${accountId}/default`
    );
    return res.data;
  }

  /* ================= WITHDRAWALS ================= */

  async requestWithdrawal(payload = {}) {
    const res = await axiosInstance.post(
      ENDPOINTS.PROVIDERS.WITHDRAWALS,
      payload
    );
    return res.data;
  }

  async getWithdrawals(params = {}) {
    const res = await axiosInstance.get(
      ENDPOINTS.PROVIDERS.WITHDRAWALS,
      { params }
    );
    return res.data;
  }

  async getWithdrawalReceipt(transactionId) {
    const res = await axiosInstance.get(
      `${ENDPOINTS.PROVIDERS.WITHDRAWALS}/${transactionId}/receipt`
    );
    return res.data;
  }
}

const providersService = new ProvidersService();
export default providersService;
